const MOBILE_QUERY = '(max-width: 749px)';
const OPEN_CLASS = 'is-open';
const CARD_GAP = 12;
const EDGE_GAP = 12;
const SCENE_AUTOPLAY_DELAY = 5000;
const INITIALIZED_KEY = 'kompacPhsInitialized';
const sectionControllers = new WeakMap();

function isMobile() {
  return window.matchMedia(MOBILE_QUERY).matches;
}

function getFocusable(container) {
  return Array.from(
    container.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
  );
}

function clamp(value, min, max) {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

class KompacProductHotspots {
  constructor(section) {
    this.section = section;
    this.scene = section.querySelector('[data-kompac-phs-scene]');
    this.sceneCarousel = section.querySelector('[data-kompac-phs-scene-carousel]');
    this.sceneDots = Array.from(section.querySelectorAll('[data-kompac-phs-scene-dot]'));
    this.scenePrevious = section.querySelector('[data-kompac-phs-scene-prev]');
    this.sceneNext = section.querySelector('[data-kompac-phs-scene-next]');
    this.mobileTrack = section.querySelector('[data-kompac-phs-mobile-track]');
    this.mobileDots = Array.from(section.querySelectorAll('[data-kompac-phs-mobile-dot]'));
    this.items = [];
    this.activeRecord = null;
    this.closeTimer = null;
    this.sceneAutoplayTimer = null;
    this.sceneAutoplayPauseReasons = new Set();
    this.abortController = new AbortController();
    this.documentAbortController = null;
  }

  init() {
    if (!this.scene) return;

    this.items = Array.from(this.section.querySelectorAll('[data-kompac-phs-hotspot]'))
      .map((hotspot) => {
        const item = hotspot.closest('.kompac-phs__item');
        if (!item) return null;

        const card = item.querySelector('[data-kompac-phs-card]');
        const modal = item.querySelector('[data-kompac-phs-modal]');
        const backdrop = item.querySelector('[data-kompac-phs-backdrop]');
        const closeButton = item.querySelector('[data-kompac-phs-close]');
        const mobileCard = this.section.querySelector(`[data-kompac-phs-mobile-card="${item.dataset.kompacPhsItem}"]`);
        const mobileDot = this.section.querySelector(`[data-kompac-phs-mobile-dot="${item.dataset.kompacPhsItem}"]`);

        if (!card || !modal || !backdrop || !closeButton) return null;

        const scene = hotspot.closest('[data-kompac-phs-scene]');
        if (!scene) return null;

        return { item, hotspot, card, modal, backdrop, closeButton, mobileCard, mobileDot, scene };
      })
      .filter(Boolean);

    this.items.forEach((record) => this.bindRecord(record));
    this.bindSectionEvents();
    this.startSceneAutoplay();
    sectionControllers.set(this.section, this);
  }

  bindRecord(record) {
    const { signal } = this.abortController;

    record.hotspot.addEventListener('pointerenter', () => {
      if (!isMobile()) this.openDesktop(record);
    }, { signal });

    record.hotspot.addEventListener('click', (event) => {
      event.preventDefault();

      if (isMobile()) {
        this.openMobile(record);
        return;
      }

      if (this.activeRecord === record && record.item.classList.contains('is-active')) {
        this.closeRecord(record);
        return;
      }

      this.openDesktop(record);
    }, { signal });

    record.item.addEventListener('pointerleave', () => {
      if (!isMobile() && this.activeRecord === record) this.scheduleDesktopClose(record);
    }, { signal });

    record.item.addEventListener('pointerenter', () => {
      this.cancelScheduledClose();
    }, { signal });

    record.card.addEventListener('pointerenter', () => {
      this.cancelScheduledClose();
    }, { signal });

    record.card.addEventListener('pointerleave', () => {
      if (!isMobile() && this.activeRecord === record) this.scheduleDesktopClose(record);
    }, { signal });

    record.backdrop.addEventListener('click', () => {
      this.closeRecord(record, true);
    }, { signal });

    record.closeButton.addEventListener('click', () => {
      this.closeRecord(record, true);
    }, { signal });

    if (record.mobileDot) {
      record.mobileDot.addEventListener('click', (event) => {
        event.preventDefault();
        this.openMobile(record);
      }, { signal });
    }
  }

  bindSectionEvents() {
    const { signal } = this.abortController;

    window.addEventListener('resize', () => {
      if (!this.activeRecord) return;

      if (isMobile()) {
        this.closeDesktopStates();
        return;
      }

      if (!this.activeRecord.modal.hidden) {
        this.closeRecord(this.activeRecord);
        return;
      }

      this.positionCard(this.activeRecord);
    }, { signal });

    if (this.mobileTrack) {
      this.mobileTrack.addEventListener('scroll', () => {
        if (!isMobile()) return;
        window.requestAnimationFrame(() => this.syncMobileActiveFromScroll());
      }, { signal });
    }

    this.scenePrevious?.addEventListener('click', () => this.scrollScene(-1), { signal });
    this.sceneNext?.addEventListener('click', () => this.scrollScene(1), { signal });
    this.sceneDots.forEach((dot) => {
      dot.addEventListener('click', () => this.scrollToScene(dot.dataset.kompacPhsSceneDot), { signal });
    });

    this.sceneCarousel?.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => this.syncSceneActiveFromScroll());
    }, { signal });

    this.sceneCarousel?.addEventListener('pointerenter', () => this.pauseSceneAutoplay('pointer'), { signal });
    this.sceneCarousel?.addEventListener('pointerleave', () => this.resumeSceneAutoplay('pointer'), { signal });
    this.sceneCarousel?.addEventListener('pointerdown', () => this.pauseSceneAutoplay('interaction'), { signal });
    this.sceneCarousel?.addEventListener('pointerup', () => this.resumeSceneAutoplay('interaction'), { signal });
    this.sceneCarousel?.addEventListener('pointercancel', () => this.resumeSceneAutoplay('interaction'), { signal });

    this.section.addEventListener('focusin', () => this.pauseSceneAutoplay('focus'), { signal });
    this.section.addEventListener('focusout', (event) => {
      if (!this.section.contains(event.relatedTarget)) this.resumeSceneAutoplay('focus');
    }, { signal });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseSceneAutoplay('visibility');
      else this.resumeSceneAutoplay('visibility');
    }, { signal });
  }

  bindDocumentEvents() {
    this.unbindDocumentEvents();
    this.documentAbortController = new AbortController();
    const { signal } = this.documentAbortController;

    document.addEventListener('click', (event) => {
      if (!this.activeRecord) return;
      if (!(event.target instanceof Node)) return;
      if (this.activeRecord.item.contains(event.target)) return;

      this.closeActiveRecord();
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (!this.activeRecord) return;

      if (event.key === 'Escape') {
        this.closeActiveRecord(true);
        return;
      }

      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }, { signal });
  }

  unbindDocumentEvents() {
    if (!this.documentAbortController) return;

    this.documentAbortController.abort();
    this.documentAbortController = null;
  }

  openDesktop(record) {
    this.cancelScheduledClose();
    this.closeOtherRecords(record);
    this.activeRecord = record;
    record.item.classList.add('is-active');
    record.hotspot.setAttribute('aria-expanded', 'true');
    this.positionCard(record);
    this.bindDocumentEvents();
  }

  openMobile(record) {
    this.cancelScheduledClose();
    this.activeRecord = record;
    record.item.classList.remove('is-active');
    record.hotspot.setAttribute('aria-expanded', 'true');
    this.items.forEach((itemRecord) => {
      if (itemRecord !== record) itemRecord.hotspot.setAttribute('aria-expanded', 'false');
    });
    this.updateMobileActive(record);

    this.scrollMobileCardIntoTrack(record);
  }

  closeOtherRecords(currentRecord) {
    this.items.forEach((record) => {
      if (record !== currentRecord) this.closeRecord(record);
    });
  }

  closeActiveRecord(restoreFocus = false) {
    if (!this.activeRecord) return;
    this.closeRecord(this.activeRecord, restoreFocus);
  }

  closeRecord(record, restoreFocus = false) {
    const wasActive = this.activeRecord === record;

    record.item.classList.remove('is-active');
    record.hotspot.setAttribute('aria-expanded', 'false');
    record.backdrop.hidden = true;
    record.modal.hidden = true;
    record.backdrop.classList.remove(OPEN_CLASS);
    record.modal.classList.remove(OPEN_CLASS);

    if (wasActive) {
      this.activeRecord = null;
      this.unbindDocumentEvents();
    }

    if (restoreFocus) record.hotspot.focus();
  }

  updateMobileActive(activeRecord) {
    this.items.forEach((record) => {
      const isActive = record === activeRecord;
      record.mobileCard?.classList.toggle('is-active', isActive);
      record.mobileDot?.classList.toggle('is-active', isActive);
      if (record.mobileDot) {
        if (isActive) record.mobileDot.setAttribute('aria-current', 'true');
        else record.mobileDot.removeAttribute('aria-current');
      }
    });
  }

  scrollMobileCardIntoTrack(record) {
    if (!this.mobileTrack || !record.mobileCard) return;

    const left = record.mobileCard.offsetLeft - this.mobileTrack.offsetLeft;
    this.mobileTrack.scrollTo({
      left,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  scrollScene(direction) {
    const slides = this.getSceneSlides();
    if (!this.sceneCarousel || slides.length < 2) return;

    const nextIndex = (this.getActiveSceneIndex(slides) + direction + slides.length) % slides.length;
    this.scrollToScene(slides[nextIndex].dataset.kompacPhsSlide);
  }

  getSceneSlides() {
    return Array.from(this.section.querySelectorAll('[data-kompac-phs-slide]'));
  }

  getActiveSceneIndex(slides = this.getSceneSlides()) {
    if (!this.sceneCarousel || !slides.length) return 0;

    const carouselRect = this.sceneCarousel.getBoundingClientRect();
    const carouselCenter = carouselRect.left + carouselRect.width / 2;
    let activeIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - carouselCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  startSceneAutoplay() {
    if (
      !this.sceneCarousel
      || this.getSceneSlides().length < 2
      || this.sceneAutoplayPauseReasons.size
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return;

    this.stopSceneAutoplay();
    this.sceneAutoplayTimer = window.setInterval(() => this.advanceScene(), SCENE_AUTOPLAY_DELAY);
  }

  stopSceneAutoplay() {
    if (!this.sceneAutoplayTimer) return;

    window.clearInterval(this.sceneAutoplayTimer);
    this.sceneAutoplayTimer = null;
  }

  pauseSceneAutoplay(reason) {
    this.sceneAutoplayPauseReasons.add(reason);
    this.stopSceneAutoplay();
  }

  resumeSceneAutoplay(reason) {
    this.sceneAutoplayPauseReasons.delete(reason);
    this.startSceneAutoplay();
  }

  advanceScene() {
    if (!this.sceneCarousel) return;

    const slides = this.getSceneSlides();
    if (slides.length < 2) return;

    this.scrollToScene(slides[(this.getActiveSceneIndex(slides) + 1) % slides.length].dataset.kompacPhsSlide);
  }

  scrollToScene(slideId) {
    const scene = this.section.querySelector(`[data-kompac-phs-slide="${slideId}"]`);
    if (!scene || !this.sceneCarousel) return;

    this.sceneCarousel.scrollTo({
      left: scene.offsetLeft - this.sceneCarousel.offsetLeft,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  syncSceneActiveFromScroll() {
    if (!this.sceneCarousel) return;

    const carouselCenter = this.sceneCarousel.getBoundingClientRect().left + this.sceneCarousel.getBoundingClientRect().width / 2;
    let activeScene = null;
    let closestDistance = Infinity;

    this.section.querySelectorAll('[data-kompac-phs-slide]').forEach((scene) => {
      const rect = scene.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - carouselCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        activeScene = scene;
      }
    });

    if (!activeScene) return;
    this.sceneDots.forEach((dot) => {
      const isActive = dot.dataset.kompacPhsSceneDot === activeScene.dataset.kompacPhsSlide;
      dot.classList.toggle('is-active', isActive);
      if (isActive) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  syncMobileActiveFromScroll() {
    if (!this.mobileTrack || !this.items.length) return;

    const trackRect = this.mobileTrack.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let closestRecord = null;
    let closestDistance = Infinity;

    this.items.forEach((record) => {
      if (!record.mobileCard) return;

      const cardRect = record.mobileCard.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenter - trackCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestRecord = record;
      }
    });

    if (!closestRecord || closestRecord === this.activeRecord) return;

    if (!isMobile()) this.closeActiveRecord();
    this.activeRecord = closestRecord;
    this.updateMobileActive(closestRecord);
    this.items.forEach((record) => {
      record.hotspot.setAttribute('aria-expanded', record === closestRecord ? 'true' : 'false');
    });
  }

  closeDesktopStates() {
    this.items.forEach((record) => {
      record.item.classList.remove('is-active');
      if (record.modal.hidden) record.hotspot.setAttribute('aria-expanded', 'false');
    });

    if (this.activeRecord?.modal.hidden) {
      this.activeRecord = null;
      this.unbindDocumentEvents();
    }
  }

  scheduleDesktopClose(record) {
    this.cancelScheduledClose();
    this.closeTimer = window.setTimeout(() => {
      if (this.activeRecord === record && !isMobile()) this.closeRecord(record);
    }, 140);
  }

  cancelScheduledClose() {
    if (!this.closeTimer) return;

    window.clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  positionCard(record) {
    record.card.style.setProperty('--kompac-phs-card-left', '34px');
    record.card.style.setProperty('--kompac-phs-card-top', '34px');

    const sceneRect = record.scene.getBoundingClientRect();
    const itemRect = record.item.getBoundingClientRect();
    const hotspotRect = record.hotspot.getBoundingClientRect();
    const cardRect = record.card.getBoundingClientRect();

    const sceneMinLeft = sceneRect.left + EDGE_GAP;
    const sceneMaxLeft = sceneRect.right - EDGE_GAP - cardRect.width;
    const sceneMinTop = sceneRect.top + EDGE_GAP;
    const sceneMaxTop = sceneRect.bottom - EDGE_GAP - cardRect.height;

    const preferredLeft = hotspotRect.right + CARD_GAP;
    const flippedLeft = hotspotRect.left - CARD_GAP - cardRect.width;
    const preferredTop = hotspotRect.bottom + CARD_GAP;
    const flippedTop = hotspotRect.top - CARD_GAP - cardRect.height;

    let left = preferredLeft;
    if (left > sceneMaxLeft && flippedLeft >= sceneMinLeft) left = flippedLeft;
    left = clamp(left, sceneMinLeft, sceneMaxLeft);

    let top = preferredTop;
    if (top > sceneMaxTop && flippedTop >= sceneMinTop) top = flippedTop;
    top = clamp(top, sceneMinTop, sceneMaxTop);

    record.card.style.setProperty('--kompac-phs-card-left', `${left - itemRect.left}px`);
    record.card.style.setProperty('--kompac-phs-card-top', `${top - itemRect.top}px`);
  }

  trapFocus(event) {
    const record = this.activeRecord;
    if (!record || record.modal.hidden) return;

    const focusable = getFocusable(record.modal);
    if (!focusable.length) {
      event.preventDefault();
      record.modal.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  destroy() {
    this.cancelScheduledClose();
    this.stopSceneAutoplay();
    this.items.forEach((record) => this.closeRecord(record));
    this.abortController.abort();
    this.unbindDocumentEvents();
    this.section.dataset[INITIALIZED_KEY] = '';
    sectionControllers.delete(this.section);
  }
}

function initSection(section) {
  if (!section || section.dataset[INITIALIZED_KEY] === 'true') return;

  section.dataset[INITIALIZED_KEY] = 'true';
  const controller = new KompacProductHotspots(section);
  controller.init();
}

function initAllSections(root = document) {
  if (root.matches?.('[data-kompac-phs-section]')) initSection(root);

  root.querySelectorAll('[data-kompac-phs-section]').forEach(initSection);
}

initAllSections();

document.addEventListener('shopify:section:load', (event) => {
  initAllSections(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector?.('[data-kompac-phs-section]') || event.target.closest?.('[data-kompac-phs-section]');
  const controller = section ? sectionControllers.get(section) : null;
  if (controller) controller.destroy();
});
